(() => {
  angular.module('app').controller('host.views.hubMessageLog.index', HubMessageLogController);

  HubMessageLogController.$inject = [
    '$scope',
    '$uibModal',
    'appConstants',
    'abp.services.app.hubMessageLog',
    'moment',
    'Hms.HubMessageLogs.HubMessageDirection',
    'Hms.HubMessageLogs.HubMessageKind',
    'Hms.HubMessageLogs.HubMessageLeg',
    'Hms.HubMessageLogs.HubMessageStatus',
  ];

  function HubMessageLogController(
    $scope,
    $modal,
    appConstants,
    hubMessageLogSvc,
    moment,
    enumHubMessageDirection,
    enumHubMessageKind,
    enumHubMessageLeg,
    enumHubMessageStatus
  ) {
    const vm = this;
    vm.loading = 0;

    // Backend Hms.HubMessageLogs enums, surfaced through portalEnums.json [HM-11062].

    vm.enums = {
      hubMessageDirection: enumHubMessageDirection,
      hubMessageKind: enumHubMessageKind,
      hubMessageLeg: enumHubMessageLeg,
      hubMessageStatus: enumHubMessageStatus,
    };

    vm.requestParams = {
      direction: null,
      kind: null,
      leg: null,
      status: null,
      messageType: null,
      internalReference: null,
      skipCount: 0,
      maxResultCount: appConstants.grid.defaultPageSize,
    };

    // The log fills up quickly, so the page opens on today's traffic and the operator
    // widens the window from there [HM-11062].

    vm.dateRangeOptions = angular.extend({}, App.createDateRangePickerOptions(), {
      timePicker: true,
    });
    vm.dateRangeOptions.locale.format = 'L LT';
    vm.dateRangeModel = {
      startDate: moment().startOf('day'),
      endDate: moment().endOf('day'),
    };

    vm.gridOptions = {
      appScopeProvider: vm,
      paginationPageSizes: appConstants.grid.defaultPageSizes,
      paginationPageSize: appConstants.grid.defaultPageSize,
      paginationCurrentPage: 1,
      useExternalPagination: true,
      enableSorting: false,
      columnDefs: [
        {
          enableSorting: false,
          name: App.localize('Actions'),
          width: 100,
          headerCellTemplate: '<span></span>',
          cellTemplate: 'actionTemplate',
        },
        {
          displayName: App.localize('Time'),
          field: 'creationTime',
          cellFilter: "momentFormat: 'L LTS'",
          minWidth: 130,
        },
        {
          displayName: App.localize('Direction'),
          field: 'direction',
          width: 90,
          cellTemplate: 'directionTemplate',
        },
        {
          displayName: App.localize('Kind'),
          field: 'kind',
          width: 100,
          cellTemplate: 'kindTemplate',
        },
        {
          // Which hop the row records; explains why Replay is offered on one failed row
          // and not the next [HM-11062].
          displayName: App.localize('HubMessageLeg'),
          field: 'leg',
          minWidth: 150,
          cellTemplate: 'legTemplate',
        },
        {
          displayName: App.localize('MessageType'),
          field: 'messageType',
          minWidth: 200,
          cellTemplate: 'messageTypeTemplate',
        },
        {
          displayName: App.localize('InternalReference'),
          field: 'internalReference',
          minWidth: 160,
          cellTemplate: 'internalReferenceTemplate',
        },
        {
          displayName: App.localize('Status'),
          field: 'status',
          width: 95,
          cellTemplate: 'statusTemplate',
        },
        {
          displayName: App.localize('Attempts'),
          field: 'attemptCount',
          width: 80,
        },
        {
          displayName: App.localize('LastAttempt'),
          field: 'lastAttemptTime',
          cellFilter: "momentFormat: 'L LTS'",
          minWidth: 130,
        },
        {
          displayName: App.localize('ErrorMessage'),
          field: 'errorSummary',
          minWidth: 200,
        },
      ],
      onRegisterApi(gridApi) {
        $scope.gridApi = gridApi;
        gridApi.pagination.on.paginationChanged(
          $scope,
          App.setPagination(vm.requestParams, getHubMessageLogs)
        );
      },
      data: [],
    };

    vm.getHubMessageLogs = getHubMessageLogs;
    vm.search = search;
    vm.viewMessage = viewMessage;
    vm.replayMessage = replayMessage;

    // Short type name for the grid; the full CLR name is in the detail modal.

    vm.shortTypeName = (messageType) => {
      if (!messageType) return messageType;
      const lastDot = messageType.lastIndexOf('.');
      return lastDot < 0 ? messageType : messageType.substring(lastDot + 1);
    };

    init();

    function init() {
      getHubMessageLogs();
    }

    function buildFilterInput() {
      const input = {
        skipCount: vm.requestParams.skipCount,
        maxResultCount: vm.requestParams.maxResultCount,
      };

      if (vm.requestParams.direction !== null) input.direction = vm.requestParams.direction;
      if (vm.requestParams.kind !== null) input.kind = vm.requestParams.kind;
      if (vm.requestParams.leg !== null) input.leg = vm.requestParams.leg;
      if (vm.requestParams.status !== null) input.status = vm.requestParams.status;
      if (vm.requestParams.messageType) input.messageType = vm.requestParams.messageType;

      // The server matches an internal reference either as a Guid or as free text, so the
      // box takes whatever the engineer pastes in [HM-11062].

      if (vm.requestParams.internalReference) {
        input.internalReference = vm.requestParams.internalReference.trim();
      }

      // Send the picker's own bounds, offset and all. The server compares them against a
      // local-time CreationTime and treats the upper bound as exclusive, so a date-only
      // string would arrive as midnight and drop the whole of the closing day [HM-11062].

      if (vm.dateRangeModel.startDate) {
        input.creationTimeFrom = vm.dateRangeModel.startDate.format();
      }

      if (vm.dateRangeModel.endDate) {
        input.creationTimeTo = vm.dateRangeModel.endDate.format();
      }

      return input;
    }

    // A new set of filters is a new result set, so go back to the first page. Searching
    // from page 3 would otherwise ask for rows the narrowed result no longer has and
    // render an empty grid [HM-11062].
    //
    // Leaving the current page makes ui-grid raise paginationChanged on the next digest,
    // and that handler already resets skipCount and fetches; fetching here as well would
    // put the same request on the wire twice. Only page 1 needs the direct call, because
    // setting the page to the value it already holds raises nothing.

    function search() {
      if (vm.gridOptions.paginationCurrentPage !== 1) {
        vm.gridOptions.paginationCurrentPage = 1;
        return;
      }

      vm.requestParams.skipCount = 0;
      getHubMessageLogs();
    }

    function getHubMessageLogs() {
      if (vm.loading) return;

      vm.loading += 1;
      hubMessageLogSvc
        .getHubMessageLogs(buildFilterInput())
        .success((data) => {
          vm.gridOptions.data = data.items;
          vm.gridOptions.totalItems = data.totalCount;
        })
        .finally(() => {
          vm.loading -= 1;
        });
    }

    function viewMessage(row) {
      $modal.open({
        templateUrl: require('./viewMessage.modal.html'),
        controller: 'host.views.hubMessageLog.viewMessage.modal as vm',
        backdrop: 'static',
        size: 'lg',
        resolve: {
          logId: () => row.id,
        },
      });
    }

    function replayMessage(row) {
      // Replay is an intentional at-least-once duplicate: HubConnector consumers do not
      // de-duplicate, so a half-succeeded flow can double-submit on the Hub [HM-11062].
      abp.message.confirm(
        App.localize('ReplayHubMessageWarning'),
        App.localize('AreYouSure'),
        (result) => {
          if (!result || vm.loading) return;

          vm.loading += 1;
          hubMessageLogSvc
            .replayHubMessageLog({ id: row.id })
            .success((output) => {
              abp.notify.info(output.message);
            })
            .finally(() => {
              vm.loading -= 1;
              getHubMessageLogs();
            });
        },
        { dangerMode: true }
      );
    }
  }
})();
